import { useState, type ReactNode } from 'react';
import { TopBar } from './TopBar';
import { StatusBar } from './StatusBar';
import { useHealth } from '../../lib/dataHooks';
import { useTheme } from '../../lib/themeContext';
import { ErrorBoundary } from './ErrorBoundary';
import { useIsMobile } from '../../lib/hooks';
import { LAYOUT_PRESETS, findPreset, type LayoutPreset } from '../../lib/layout';
import { PresetProvider, type PresetCtx } from './layoutPreset';
import { readSetting, writeSetting } from '../../lib/settings';
import type { ViewId } from '../../lib/app';

const PRESET_KEY = 'layout.presetId';

export interface AppShellProps {
  /** 图表视图：图表区 + 工作区，纵向堆叠、随页面滚动 */
  chartZone: ReactNode;
  /** 整屏视图：行情 / 复盘 / 工作区 / 笔记浏览 */
  fullView: (view: ViewId, onNavigate: (v: ViewId) => void) => ReactNode;
}

/**
 * 应用外壳。
 *
 * **滚动所有权只有一处：`main`**（overflow-y-auto）。
 *
 * 改前是 `overflow-hidden` + SplitPane 上下分栏（图表区按比例占窗口、工作区
 * 塞在剩余空间）。改为整页滚动后，若各视图内部还留着
 * `min-h-0 flex-1 overflow-y-auto`，就会出现**双重滚动条** —— 外层滚到底后
 * 内层还剩一段，触摸手势也会互相抢。故各视图的内容容器改为自然高度
 * （内容多长就多长），统一由 main 滚。
 *
 * 为什么不让文档（body）滚：移动端软键盘弹出会压缩视口，文档滚动会把页头
 * 顶出视野、输入框失焦；main 作为固定高度的滚动容器则页头与状态栏不动。
 */
export function AppShell({ chartZone, fullView }: AppShellProps) {
  const isMobile = useIsMobile();
  const [view, setView] = useState<ViewId>('chart');
  // 主题在 ThemeProvider（lightweight-charts 需要具体色值，不能只靠 CSS 变量）
  const { theme, toggle: toggleTheme } = useTheme();
  const [preset, setPreset] = useState<LayoutPreset>(() =>
    findPreset(readSetting<string>(PRESET_KEY, '')),
  );

  /** 双击图表区 → 轮换到下一个高度档位（沿用改前双击分隔条的手势） */
  const cyclePreset = () => {
    const next = LAYOUT_PRESETS[(LAYOUT_PRESETS.indexOf(preset) + 1) % LAYOUT_PRESETS.length];
    setPreset(next);
    writeSetting(PRESET_KEY, next.id);
  };

  // 状态栏：服务在线状态 + 各市场最新交易日 + 估值滞后告警（L3）
  const { status: statusData } = useHealth();

  const presetValue: PresetCtx = { preset, onCyclePreset: cyclePreset, isMobile };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <TopBar
        view={view}
        onViewChange={setView}
        theme={theme}
        onThemeToggle={toggleTheme}
      />

      <ErrorBoundary name={isMobile ? '移动视图' : '桌面视图'}>
        {/* 唯一滚动容器：内容自然高度，超出即滚动 */}
        <main
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          style={{ background: 'var(--bg-surface)' }}
        >
          <PresetProvider value={presetValue}>
            {view === 'chart' ? chartZone : fullView(view, setView)}
          </PresetProvider>
        </main>
      </ErrorBoundary>

      <StatusBar data={statusData} />
    </div>
  );
}
